import React, { useState } from 'react';
import { Copy, Trash2, ArrowRight } from 'lucide-react';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';
import { translationService } from '../../services/translationService';

let SERVER_CONFIGS = {
  ENV: process.env.REACT_APP_ENV || 'prod',
  TRANSLATE_LOCAL_SERVER:
    process.env.REACT_APP_TRANSLATE_LOCAL_SERVER || 'http://localhost:6500',
  TRANSLATE_PROD_SERVER:
    process.env.REACT_APP_TRANSLATE_PROD_SERVER ||
    'https://www.translate.paralegal.lk/api',
};

const ENV = SERVER_CONFIGS.ENV;
console.log('ENV:', ENV);

const HOST =
  ENV === 'local'
    ? SERVER_CONFIGS.TRANSLATE_LOCAL_SERVER
    : SERVER_CONFIGS.TRANSLATE_PROD_SERVER;

// Function to detect language based on character patterns
const detectLanguage = (text) => {
  if (!text.trim()) return 'en'; // Default to English if empty

  // Tamil character range: \u0B80-\u0BFF
  const tamilRegex = /[\u0B80-\u0BFF]/;
  // Sinhala character range: \u0D80-\u0DFF
  const sinhalaRegex = /[\u0D80-\u0DFF]/;
  // English and other characters
  const englishRegex = /[a-zA-Z]/;

  if (tamilRegex.test(text)) return 'ta';
  if (sinhalaRegex.test(text)) return 'si';
  if (englishRegex.test(text)) return 'en';

  return 'en'; // Default to English if no specific pattern is detected
};

const Playground = () => {
  const [sourceText, setSourceText] = useState('');
  const [charCount, setCharCount] = useState(0);
  const [translatedText, setTranslatedText] = useState('');
  const [sourceLanguage, setSourceLanguage] = useState('ta');
  const [targetLanguage, setTargetLanguage] = useState('en');
  const [isLoading, setIsLoading] = useState(false);
  const [currentTaskId, setCurrentTaskId] = useState(null);
  const [progressMessage, setProgressMessage] = useState('');

  const languages = ['Sinhala', 'Tamil', 'English'];

  // Get language name from code
  const getLanguageName = (code) => {
    switch (code) {
      case 'si':
        return 'Sinhala';
      case 'ta':
        return 'Tamil';
      case 'en':
        return 'English';
      default:
        return 'English';
    }
  };

  // Get available target languages based on source language
  const getAvailableTargetLanguages = (sourceLang) => {
    return languages.filter(
      (lang) => lang.toLowerCase().substring(0, 2) !== sourceLang
    );
  };

  const LanguageButton = ({ lang, isSelected, onClick }) => (
    <button
      className={`px-4 py-2 rounded-full transition-colors ${
        isSelected
          ? 'bg-pl_prim_color_1 text-white'
          : 'text-gray-800 hover:text-white hover:bg-pl_prim_color_1'
      }`}
      onClick={onClick}
    >
      {lang}
    </button>
  );

  LanguageButton.propTypes = {
    lang: PropTypes.string.isRequired,
    isSelected: PropTypes.bool.isRequired,
    onClick: PropTypes.func,
  };

  const handleTextChange = (e) => {
    const text = e.target.value;
    setSourceText(text);
    setCharCount(text.length);

    // Automatically detect and set the source language
    const detectedLang = detectLanguage(text);
    setSourceLanguage(detectedLang);

    // If current target language is same as detected source language, switch to first available target language
    if (targetLanguage === detectedLang) {
      const availableTargets = getAvailableTargetLanguages(detectedLang);
      if (availableTargets.length > 0) {
        setTargetLanguage(availableTargets[0].toLowerCase().substring(0, 2));
      }
    }
  };

  // Poll task status function
  const pollTaskStatus = async (taskId) => {
    try {
      const statusData = await translationService.getTaskStatus(taskId);
      
      if (statusData.status === 'SUCCESS' || statusData.status === 'COMPLETED') {
        // Task completed successfully
        setTranslatedText(statusData.result?.translated_text || '');
        setIsLoading(false);
        setCurrentTaskId(null);
        setProgressMessage('');
        toast.success('Translation completed!');
        return;
      }
      
      if (statusData.status === 'FAILED') {
        // Task failed
        setIsLoading(false);
        setCurrentTaskId(null);
        setProgressMessage('');
        toast.error(`Translation failed: ${statusData.error || 'Unknown error'}`);
        return;
      }
      
      // Task still processing, update progress message
      if (statusData.status === 'PROCESSING') {
        const meta = statusData.result || {};
        setProgressMessage(meta.status || 'Processing translation...');
      } else if (statusData.status === 'PENDING') {
        setProgressMessage('Translation queued...');
      }
      
      // Continue polling after 10 seconds
      setTimeout(() => {
        pollTaskStatus(taskId);
      }, 10000);
      
    } catch (error) {
      console.error('Error polling task status:', error);
      setIsLoading(false);
      setCurrentTaskId(null);
      setProgressMessage('');
      toast.error('Failed to check translation status');
    }
  };

  const handleTranslate = async () => {
    if (!sourceText.trim()) {
      toast.error('Please enter some text to translate');
      return;
    }

    if (sourceLanguage === targetLanguage) {
      toast.error('Source and target languages must be different');
      return;
    }

    setIsLoading(true);
    setProgressMessage('Starting translation...');
    setTranslatedText('');

    try {
      const response = await fetch(`${HOST}/play`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          text: sourceText,
          source_to_target: `${sourceLanguage}_${targetLanguage}`,
        }),
      });

      if (!response.ok) throw new Error('Translation failed');

      const data = await response.json();
      
      if (data.task_id) {
        // New async mode - start polling
        setCurrentTaskId(data.task_id);
        pollTaskStatus(data.task_id);
      } else {
        // Old sync mode fallback (shouldn't happen anymore)
        setTranslatedText(data.translated_text || '');
        setIsLoading(false);
        setProgressMessage('');
      }
    } catch (error) {
      setIsLoading(false);
      setProgressMessage('');
      setCurrentTaskId(null);
      toast.error('Translation failed. Please try again.');
      console.error('Translation error:', error);
    }
  };

  return (
    <div className="container mx-auto px-4 max-w-7xl h-full flex items-center justify-center">
        <div className="flex flex-col gap-2 w-full">
            {/* Title */}
            <div className="font-poppins px-6">
              <div className="relative">
                <h2 className="text-3xl font-semibold text-center text-gray-500 mb-1 mt-1">

                  Translate Text
                </h2>
              </div>
            </div>

            {/* Text Areas with Language Selections */}
            <div className="grid grid-cols-1 text-pl_txt_light_1 md:grid-cols-2 gap-4 relative">
              <div className="flex flex-col gap-4">
                {/* Source Language Selection */}
                <div className="flex text-pl_txt_light_1 justify-center">
                  <div className="flex gap-2 text-pl_txt_light_1">
                    {sourceText.trim() === '' ? (
                      languages.map((lang) => (
                        <LanguageButton
                          key={lang}
                          lang={lang}
                          isSelected={
                            sourceLanguage === lang.toLowerCase().substring(0, 2)
                          }
                          onClick={() =>
                            setSourceLanguage(lang.toLowerCase().substring(0, 2))
                          }
                        />
                      ))
                    ) : (
                      <LanguageButton
                        lang={getLanguageName(sourceLanguage)}
                        isSelected={true}
                        onClick={null}
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 hidden md:block">
                <ArrowRight className="text-pl_txt_light_1" size={24} />
              </div>
              <div className="hidden md:block flex-col gap-4 text-pl_txt_light_1">
                {/* Target Language Selection */}
                <div className="flex justify-center">
                  <div className="flex flex-wrap gap-2 mb-2 md:mb-0 md:flex-nowrap">
                    {sourceText.trim() === ''
                      ? languages.map((lang) => (
                          <LanguageButton
                            key={lang}
                            lang={lang}
                            isSelected={
                              targetLanguage ===
                              lang.toLowerCase().substring(0, 2)
                            }
                            onClick={() =>
                              setTargetLanguage(
                                lang.toLowerCase().substring(0, 2)
                              )
                            }
                          />
                        ))
                      : getAvailableTargetLanguages(sourceLanguage).map(
                          (lang) => (
                            <LanguageButton
                              key={lang}
                              lang={lang}
                              isSelected={
                                targetLanguage ===
                                lang.toLowerCase().substring(0, 2)
                              }
                              onClick={() =>
                                setTargetLanguage(
                                  lang.toLowerCase().substring(0, 2)
                                )
                              }
                            />
                          )
                        )}
                  </div>
                </div>
              </div>
            </div>

            {/* Text Areas */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex flex-col">
                <textarea
                  value={sourceText}
                  onChange={e => {
                    if (e.target.value.length <= 3000) {
                      handleTextChange(e);
                    }
                  }}
                  maxLength={3000}
                  placeholder="Enter text to translate..."
                  className="w-full h-[200px] p-4 flex items-center justify-between bg-white/90 backdrop-blur-sm rounded-lg shadow-md border-2 border-dashed border-pl_txt_light_3 hover:border-pl_prim_color_1 outline-none "
                />
                <button
                  onClick={() => setSourceText('')}
                  className="text-pl_txt_light_3  hover:text-pl_prim_color_1 flex items-center gap-2 "
                >
                  <Trash2 size={16} /> Clear
                </button>
                <span className={`text-sm ${charCount === 3000 ? 'text-red-500' : 'text-pl_txt_dark_3'}`}>
                  {charCount} / 3000 characters
                </span>
              </div>
              <div className="md:hidden flex flex-col gap-4 text-pl_txt_light_1">
                
                {/* Target Language Selection - Show all three if no text, else available targets */}
                <div className="flex justify-center">
                  <div className="flex flex-wrap gap-1 mb-2 md:mb-0 md:flex-nowrap">
                    {sourceText.trim() === ''
                      ? languages.map((lang) => (
                          <LanguageButton
                            key={lang}
                            lang={lang}
                            isSelected={
                              targetLanguage ===
                              lang.toLowerCase().substring(0, 2)
                            }
                            onClick={() =>
                              setTargetLanguage(
                                lang.toLowerCase().substring(0, 2)
                              )
                            }
                          />
                        ))
                      : getAvailableTargetLanguages(sourceLanguage).map(
                          (lang) => (
                            <LanguageButton
                              key={lang}
                              lang={lang}
                              isSelected={
                                targetLanguage ===
                                lang.toLowerCase().substring(0, 2)
                              }
                              onClick={() =>
                                setTargetLanguage(
                                  lang.toLowerCase().substring(0, 2)
                                )
                              }
                            />
                          )
                        )}
                  </div>
                </div>
              </div>
              <div className="flex flex-col">
                <textarea
                  value={translatedText}
                  readOnly
                  placeholder="Translation will appear here..."
                  className="w-full h-[200px] p-4 bg-pl_bg_light_3 text-pl_txt_light_1 rounded-xl resize-none border-2 border-dashed border-pl_txt_light_3 hover:border-pl_prim_color_1 outline-none "
                />
                <button
                  onClick={() => navigator.clipboard.writeText(translatedText)}
                  className="text-pl_txt_light_3 hover:text-pl_prim_color_1 flex items-center gap-2"
                >
                  <Copy size={16} /> Copy
                </button>
              </div>
            </div>

            {/* Translate Button */}
            <div className="flex justify-center">
              <button
                onClick={handleTranslate}
                disabled={isLoading}
                className="bg-pl_prim_color_1 text-pl_bg_light_3 px-8 py-2 rounded-full hover:bg-pl_prim_color_2 disabled:opacity-50 w-full md:w-auto "
              >
                {isLoading ? (progressMessage || 'Translating...') : 'Translate'}
              </button>
            </div>
        </div>
      </div>
  );
};

export default Playground;
